import { asyncStoreGet } from './asyncStorage';
import { TOKEN_KEY } from '../contants/authKeys';

/**
 * Decode JWT token to extract user information
 * @param {string} token - JWT token
 * @returns {object|null} - Decoded payload or null if invalid
 */
const decodeToken = (token) => {
    try {
        const base64Url = token.split('.')[1];
        if (!base64Url) return null;
        
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (err) {
        console.warn('[tokenUtils] Failed to decode token:', err);
        return null;
    }
};

/**
 * Get stored authentication token
 * @returns {Promise<string|null>} - Token or null if not found
 */
export const getToken = async () => {
    try {
        const token = await asyncStoreGet(TOKEN_KEY);
        if (!token) {
            console.debug('[tokenUtils] No token found in storage');
            return null;
        }
        console.debug('[tokenUtils] Token found, length:', token.length);
        return token;
    } catch (err) {
        console.warn('[tokenUtils] Error getting token:', err);
        return null;
    }
};

/**
 * Get userId from stored authentication token
 * @returns {Promise<string|null>} - userId or null if not found
 */
export const getUserIdFromToken = async () => {
    try {
        const token = await getToken();
        if (!token) {
            return null;
        }
        
        const decoded = decodeToken(token);
        if (!decoded) {
            console.warn('[tokenUtils] Failed to decode token');
            return null;
        }
        
        console.debug('[tokenUtils] Decoded token payload:', decoded);
        
        // Try common JWT claims for userId
        const userId = decoded.userId || 
                       decoded.user_id || 
                       decoded.sub || 
                       decoded.id || 
                       null;
        
        if (!userId) {
            console.warn('[tokenUtils] No userId found in token. Available fields:', Object.keys(decoded));
        }
        
        return userId;
    } catch (err) {
        console.warn('[tokenUtils] Error getting userId from token:', err);
        return null;
    }
};

/**
 * Get complete decoded token
 * @returns {Promise<object|null>} - Decoded token or null if invalid
 */
export const getDecodedToken = async () => {
    try {
        const token = await getToken();
        if (!token) return null;
        return decodeToken(token);
    } catch (err) {
        console.warn('[tokenUtils] Error getting decoded token:', err);
        return null;
    }
};
